import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useInventory, InventoryFilters } from '../contexts/InventoryContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { BarcodeScanner } from '../components/ui/BarcodeScanner';
import { ExternalScanner } from '../components/ui/ExternalScanner';
import { useOfflineGuard } from '../components/ui/OfflineBanner';
import { AddItemForm } from '../components/inventory/AddItemForm';
import { LocationQuantityDisplay } from '../components/inventory/LocationQuantityDisplay';
import { MergeDuplicateModal } from '../components/inventory/merge-duplicate-modal';
import type { DeletionBlocker } from '../lib/product-deletion';
import { Package, Plus, Edit3, Trash2, Search, Camera, Zap, AlertTriangle, Palette, Ruler, X, Copy, Layers, Check, Image, ArrowUpDown, ArrowUp, ArrowDown, WifiOff, MapPin, Tag, Pencil, Filter, ChevronDown, ChevronUp, RotateCcw, TrendingUp, TrendingDown, DollarSign, PackageX, ChevronLeft, ChevronRight, MoreHorizontal, SlidersHorizontal, RefreshCw, Cloud, CloudOff, Loader2, Hash, Box, Link2, Upload } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getAdminId } from '../utils/getAdminId';
import {
  fetchLocationQuantitiesForProducts,
  migrateAllProductsToWarehouse,
  ensureDefaultLocations,
  type LocationQuantity
} from '../utils/locationInventory';
import { getProductsBinLocations } from '../lib/services/binInventoryService';
import { BinLocationDisplay } from '../components/inventory/BinLocationBadge';
import type { ProductBinLocation } from '../types/warehouse';

interface ProductColor {
  id: string;
  name: string;
  hex_code?: string;
}

interface LocationComparison {
  productLocationId: string;
  locationName: string;
  shopifyLocationId: string;
  localQty: number;
  shopifyQty: number | null;
}

interface ProductSize {
  id: string;
  name: string;
  sort_order: number;
}

interface ProductLocation {
  id: string;
  name: string;
}

interface ProductCategory {
  id: string;
  name: string;
}

export const Inventory: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { items, allItems, allItemsLoading, addItem, updateItem, deleteItem, refreshInventory, pagination, loadPage, loading: inventoryLoading, fetchAllItems } = useInventory();
  const { isNetworkOffline, guardAsyncAction } = useOfflineGuard();
  const { success, error, warning } = useNotification();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [showAddForm, setShowAddForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showExternalScanner, setShowExternalScanner] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [stockFilter, setStockFilter] = useState<'all' | 'in_stock' | 'low_stock' | 'out_of_stock'>('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'variants'>('table');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [totalStats, setTotalStats] = useState({ totalItems: 0, totalQuantity: 0, lowStock: 0, outOfStock: 0, totalValue: 0 });

  const [colors, setColors] = useState<ProductColor[]>([]);
  const [sizes, setSizes] = useState<ProductSize[]>([]);
  const [showColorModal, setShowColorModal] = useState(false);
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [showDuplicateSizeModal, setShowDuplicateSizeModal] = useState(false);
  const [duplicateSizeSearch, setDuplicateSizeSearch] = useState('');
  const [selectedItemForDuplicate, setSelectedItemForDuplicate] = useState<any>(null);
  const [duplicateSizeProgress, setDuplicateSizeProgress] = useState<{ current: number; total: number; status: string } | null>(null);
  const [blockedDeletion, setBlockedDeletion] = useState<{
    item: { id: string; name: string; sku?: string; quantity: number };
    blockers: DeletionBlocker[];
  } | null>(null);

  const allStandardSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const [selectedSizesForDuplicate, setSelectedSizesForDuplicate] = useState<Set<string>>(new Set(allStandardSizes));
  const [newColorName, setNewColorName] = useState('');
  const [newColorHex, setNewColorHex] = useState('#000000');
  const [newSizeName, setNewSizeName] = useState('');
  const [newSizeOrder, setNewSizeOrder] = useState(0);
  const [editingColorId, setEditingColorId] = useState<string | null>(null);
  const [editColorName, setEditColorName] = useState('');
  const [editColorHex, setEditColorHex] = useState('#000000');

  const [locations, setLocations] = useState<ProductLocation[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editLocationName, setEditLocationName] = useState('');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  const [locationQuantitiesMap, setLocationQuantitiesMap] = useState<Map<string, LocationQuantity[]>>(new Map());
  const [migrationDone, setMigrationDone] = useState(false);
  const migrationRunRef = useRef(false);
  const [repairingWarehouse, setRepairingWarehouse] = useState(false);
  const [shopifyMappings, setShopifyMappings] = useState<Map<string, { lastPushedQty: number | null; lastPushedAt: string | null; shopifyInventoryItemId: string | null; shopifyQty: number | null }>>(new Map());
  const [shopifyShopId, setShopifyShopId] = useState<string | null>(null);
  const [shopifyLocComparison, setShopifyLocComparison] = useState<Map<string, LocationComparison[]>>(new Map());
  const [shopifyStatus, setShopifyStatus] = useState<Map<string, 'matchable' | 'tracking_off' | 'not_found'>>(new Map());
  const [discoveringSkus, setDiscoveringSkus] = useState(false);
  const [syncingProductId, setSyncingProductId] = useState<string | null>(null);
  const [shopConnected, setShopConnected] = useState(false);
  const [binLocationsMap, setBinLocationsMap] = useState<Map<string, ProductBinLocation>>(new Map());

  const adminId = getAdminId(user);

  useEffect(() => {
    if (adminId) {
      fetchColors();
      fetchSizes();
      fetchLocations();
      fetchProductCategories();
      fetchAllItems();
    }
  }, [adminId, fetchAllItems]);

  useEffect(() => {
    const runMigration = async () => {
      if (!adminId || migrationRunRef.current) return;
      migrationRunRef.current = true;
      await ensureDefaultLocations(adminId);
      const migratedCount = await migrateAllProductsToWarehouse(adminId);
      if (migratedCount > 0) {
        console.log(`Migrated ${migratedCount} products to Warehouse location`);
      }
      setMigrationDone(true);
    };
    runMigration();
  }, [adminId]);

  // Manual re-run of the warehouse-row backfill. Useful after this update ships
  // because B2B Portal now requires a product_location_inventory row at the
  // warehouse to display the item; legacy products with stock only in
  // products.quantity get picked up here.
  const handleRepairWarehouse = useCallback(async () => {
    if (!adminId || repairingWarehouse) return;
    setRepairingWarehouse(true);
    try {
      await ensureDefaultLocations(adminId);
      const count = await migrateAllProductsToWarehouse(adminId);
      if (count > 0) {
        success(`Repaired Warehouse assignments for ${count} product${count === 1 ? '' : 's'}.`);
        await refreshInventory();
      } else {
        success('Warehouse assignments already up to date.');
      }
    } catch (err) {
      console.error('Repair warehouse error:', err);
      error('Failed to repair Warehouse assignments. Please try again.');
    } finally {
      setRepairingWarehouse(false);
    }
  }, [adminId, repairingWarehouse, refreshInventory, success, error]);

  const applyFilters = useCallback((newPage?: number) => {
    const filters: InventoryFilters = {
      searchTerm: searchTerm || undefined,
      category: selectedCategory || undefined,
      colorId: selectedColor || undefined,
      sizeId: selectedSize || undefined,
      stockFilter: stockFilter,
      sortColumn: sortColumn || undefined,
      sortDirection: sortDirection
    };
    loadPage(newPage || 1, pagination.pageSize, filters);
  }, [searchTerm, selectedCategory, selectedColor, selectedSize, stockFilter, sortColumn, sortDirection, loadPage, pagination.pageSize]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      applyFilters(1);
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    applyFilters(1);
  }, [selectedCategory, selectedColor, selectedSize, stockFilter]);

  useEffect(() => {
    if (sortColumn) {
      applyFilters(pagination.currentPage);
    }
  }, [sortColumn, sortDirection]);

  const fetchLocationQuantities = useCallback(async () => {
    if (!adminId || items.length === 0) {
      setLocationQuantitiesMap(new Map());
      return;
    }
    const productIds = items.map(item => item.id);
    const quantitiesMap = await fetchLocationQuantitiesForProducts(productIds, adminId);
    setLocationQuantitiesMap(quantitiesMap);
  }, [adminId, items]);

  const fetchBinLocations = useCallback(async () => {
    if (items.length === 0) {
      setBinLocationsMap(new Map());
      return;
    }
    const productIds = items.map(item => item.id);
    const locationsMap = await getProductsBinLocations(productIds);
    setBinLocationsMap(locationsMap);
  }, [items]);

  const fetchShopifyLocationComparison = useCallback(async () => {
    if (!shopConnected || !shopifyShopId || items.length === 0) {
      setShopifyLocComparison(new Map());
      return;
    }
    const productIds = items.map(item => item.id);
    const { data, error } = await supabase.rpc('get_shopify_location_comparison', {
      p_shop_id: shopifyShopId,
      p_product_ids: productIds,
    });
    if (error) {
      console.error('Failed to fetch Shopify location comparison:', error);
      return;
    }
    const map = new Map<string, LocationComparison[]>();
    for (const row of data || []) {
      const entry: LocationComparison = {
        productLocationId: row.product_location_id,
        locationName: row.location_name || 'Location',
        shopifyLocationId: String(row.shopify_location_id),
        localQty: row.local_qty ?? 0,
        shopifyQty: row.shopify_qty ?? null,
      };
      const arr = map.get(row.product_id);
      if (arr) arr.push(entry);
      else map.set(row.product_id, [entry]);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.locationName.localeCompare(b.locationName));
    }
    setShopifyLocComparison(map);
  }, [shopConnected, shopifyShopId, items]);

  useEffect(() => {
    fetchShopifyLocationComparison();
  }, [fetchShopifyLocationComparison]);

  useEffect(() => {
    if (migrationDone) {
      fetchLocationQuantities();
      fetchBinLocations();
    }
  }, [migrationDone, items, fetchLocationQuantities, fetchBinLocations]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!adminId) return;
      const { data, error } = await supabase.rpc('get_inventory_stats_v2', { p_admin_id: adminId });
      if (!error && data && Array.isArray(data) && data.length > 0) {
        const result = data[0];
        setTotalStats({
          totalItems: result.total_items || 0,
          totalQuantity: result.total_quantity || 0,
          lowStock: result.low_stock || 0,
          outOfStock: result.out_of_stock || 0,
          totalValue: parseFloat(result.sell_value) || 0
        });
      } else if (!error && data && !Array.isArray(data)) {
        setTotalStats({
          totalItems: data.total_items || 0,
          totalQuantity: data.total_quantity || 0,
          lowStock: data.low_stock || 0,
          outOfStock: data.out_of_stock || 0,
          totalValue: parseFloat(data.sell_value) || 0
        });
      } else {
        const { count: totalCount } = await supabase.from('products').select('id', { count: 'exact', head: true }).eq('admin_id', adminId);
        const { count: lowCount } = await supabase.from('products').select('id', { count: 'exact', head: true }).eq('admin_id', adminId).gt('quantity', 0).lte('quantity', 10);
        const { count: outCount } = await supabase.from('products').select('id', { count: 'exact', head: true }).eq('admin_id', adminId).eq('quantity', 0);
        setTotalStats({
          totalItems: totalCount || 0,
          totalQuantity: 0,
          lowStock: lowCount || 0,
          outOfStock: outCount || 0,
          totalValue: 0
        });
      }
    };
    fetchStats();
  }, [adminId, items]);

  const fetchColors = async () => {
    if (!adminId) return;
    const { data } = await supabase
      .from('product_colors')
      .select('*')
      .eq('admin_id', adminId)
      .order('name');
    if (data) setColors(data);
  };

  const fetchSizes = async () => {
    if (!adminId) return;
    const { data } = await supabase
      .from('product_sizes')
      .select('*')
      .eq('admin_id', adminId)
      .order('sort_order');
    if (data) setSizes(data);
  };

  const fetchShopifyMappings = useCallback(async () => {
    if (!adminId) return;

    const { data: shops } = await supabase.rpc('get_shopify_shops', { p_admin_id: adminId });
    if (!shops || shops.length === 0) {
      setShopConnected(false);
      return;
    }

    setShopConnected(true);
    const shop = shops[0];
    setShopifyShopId(shop.id);

    const { data: mappings } = await supabase.rpc('get_inventory_maps_with_products', { p_shop_id: shop.id });
    if (mappings) {
      const mappingsMap = new Map<string, { lastPushedQty: number | null; lastPushedAt: string | null; shopifyInventoryItemId: string | null; shopifyQty: number | null }>();
      for (const m of mappings) {
        mappingsMap.set(m.product_id, {
          lastPushedQty: m.last_pushed_qty,
          lastPushedAt: m.last_pushed_at,
          shopifyInventoryItemId: m.shopify_inventory_item_id?.toString() || null,
          shopifyQty: m.last_verified_qty ?? null,
        });
      }
      setShopifyMappings(mappingsMap);
    }
  }, [adminId]);

  const discoverShopifyMatches = useCallback(async (
    currentMappings: Map<string, { lastPushedQty: number | null; lastPushedAt: string | null; shopifyInventoryItemId: string | null; shopifyQty: number | null }>
  ) => {
    if (!shopConnected || items.length === 0) return;

    const unmapped = items.filter(
      item => item.sku && !item.sku.startsWith('SHOP-') && !currentMappings.has(item.id)
    );
    if (unmapped.length === 0) return;

    setDiscoveringSkus(true);
    try {
      const token = localStorage.getItem('auth_token');
      const allStatuses = new Map<string, 'matchable' | 'tracking_off' | 'not_found'>();

      for (let i = 0; i < unmapped.length; i += 100) {
        const batch = unmapped.slice(i, i + 100).map(item => ({
          productId: item.id,
          sku: item.sku!,
        }));

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-inventory/discover`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ skus: batch }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.statuses) {
            for (const [productId, status] of Object.entries(data.statuses)) {
              allStatuses.set(productId, status as 'matchable' | 'tracking_off' | 'not_found');
            }
          } else if (data.matches) {
            for (const [productId, found] of Object.entries(data.matches)) {
              allStatuses.set(productId, found ? 'matchable' : 'not_found');
            }
          }
        }
      }

      setShopifyStatus(allStatuses);
    } catch (err) {
      console.error('Failed to discover Shopify matches:', err);
    } finally {
      setDiscoveringSkus(false);
    }
  }, [shopConnected, items]);

  useEffect(() => {
    if (adminId) {
      fetchShopifyMappings();
    }
  }, [adminId, fetchShopifyMappings]);

  const shopifyMappingsSize = shopifyMappings.size;
  useEffect(() => {
    if (shopConnected && items.length > 0) {
      discoverShopifyMatches(shopifyMappings);
    }
  }, [shopConnected, items.length, shopifyMappingsSize]);

  const syncProductToShopify = async (productId: string) => {
    if (!adminId || syncingProductId) return;

    // Get product details
    const product = items.find(p => p.id === productId);
    if (!product) return;

    // Check if product has valid SKU
    if (!product.sku || product.sku.startsWith('SHOP-')) {
      error('Product needs a valid SKU to sync with Shopify');
      return;
    }

    setSyncingProductId(productId);
    try {
      const token = localStorage.getItem('auth_token');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-inventory/push`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ productId }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        error(result.error || 'Failed to sync product to Shopify');
        return;
      }

      if (result.pushed) {
        await fetchShopifyMappings();
        await fetchShopifyLocationComparison();
        const locCount = result.pushed.locationsPushed ?? 1;
        success(
          locCount > 1
            ? `Product "${product.sku}" synced to ${locCount} locations`
            : `Product "${product.sku}" successfully synced to Shopify`
        );
        return;
      }

      if (result.error) {
        if (result.error.includes('SKU not found') || result.error.includes('not found in Shopify') || result.error.includes('Product not mapped')) {
          error(`SKU "${product.sku}" not found in Shopify. Please ensure the product exists and is imported from Shopify.`);
        } else {
          error(result.error);
        }
        return;
      }

      warning('Sync completed but product quantity was not updated. Please check if the product is properly mapped.');
    } catch (err) {
      error('Failed to sync product to Shopify. Please try again.');
    } finally {
      setSyncingProductId(null);
    }
  };

  const [linkingAll, setLinkingAll] = useState(false);
  const [refreshingQty, setRefreshingQty] = useState(false);

  const handleRefreshShopifyQty = async () => {
    setRefreshingQty(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-refresh-qty`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const detail = await response.text();
        console.error('Refresh Shopify qty failed:', detail);
        error('Failed to refresh Shopify quantities');
        return;
      }

      await fetchShopifyLocationComparison();
      // Large catalogs are processed in self-continuing chunks; pick up later
      // batches with a couple of delayed refetches.
      setTimeout(() => { void fetchShopifyLocationComparison(); }, 8000);
      setTimeout(() => { void fetchShopifyLocationComparison(); }, 20000);
      success('Refreshing Shopify quantities - comparison updates as data arrives');
    } catch (err) {
      console.error('Failed to refresh Shopify quantities:', err);
      error('Failed to refresh Shopify quantities');
    } finally {
      setRefreshingQty(false);
    }
  };

  const handleLinkAllMatching = async () => {
    const matchableIds = Array.from(shopifyStatus.entries())
      .filter(([, status]) => status === 'matchable')
      .map(([productId]) => productId);

    if (matchableIds.length === 0) return;

    setLinkingAll(true);
    let synced = 0;
    let failed = 0;
    const failureReasons = new Map<string, number>();

    try {
      const token = localStorage.getItem('auth_token');
      const CHUNK_SIZE = 150;

      for (let i = 0; i < matchableIds.length; i += CHUNK_SIZE) {
        const chunk = matchableIds.slice(i, i + CHUNK_SIZE);

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-inventory/push-all`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ productIds: chunk }),
          }
        );

        const result = await response.json();

        if (!response.ok) {
          console.error('Link All chunk failed:', result);
          failed += chunk.length;
          const reason = result.error || 'Push-all request failed';
          failureReasons.set(reason, (failureReasons.get(reason) || 0) + chunk.length);
          continue;
        }

        const summary = result.summary || {};
        synced += (summary.verified || 0) + (summary.mismatched || 0);
        failed += summary.failed || 0;

        if (Array.isArray(result.results)) {
          for (const row of result.results) {
            if (row.status === 'failed' || row.status === 'skipped') {
              const reason = row.error || row.status;
              console.error('Link All product failed:', row.sku || row.productId, reason);
              failureReasons.set(reason, (failureReasons.get(reason) || 0) + 1);
            }
          }
        }

        if (Array.isArray(result.warnings)) {
          for (const warningMsg of result.warnings) {
            console.warn('Link All warning:', warningMsg);
          }
        }
      }

      await fetchShopifyMappings();
      await fetchShopifyLocationComparison();
      setShopifyStatus(new Map());

      if (synced > 0) {
        const reasonSummary = Array.from(failureReasons.entries())
          .slice(0, 3)
          .map(([reason, count]) => `${count} ${reason}`)
          .join(', ');
        success(
          `Linked and synced ${synced} product${synced !== 1 ? 's' : ''} to Shopify${
            failed > 0 ? ` (${failed} failed${reasonSummary ? `: ${reasonSummary}` : ''})` : ''
          }`
        );
      } else {
        const reasonSummary = Array.from(failureReasons.entries())
          .slice(0, 3)
          .map(([reason, count]) => `${count} ${reason}`)
          .join(', ');
        error(`Failed to link products to Shopify (${failed} failed${reasonSummary ? `: ${reasonSummary}` : ''})`);
      }
    } catch (err) {
      console.error('Failed to link products to Shopify:', err);
      error('Failed to link products to Shopify');
    } finally {
      setLinkingAll(false);
    }
  };

  const handleAddColor = async () => {
    if (!newColorName.trim() || !adminId) return;
    const { error } = await supabase
      .from('product_colors')
      .insert({
        admin_id: adminId,
        name: newColorName.trim(),
        hex_code: newColorHex
      });
    if (!error) {
      setNewColorName('');
      setNewColorHex('#000000');
      fetchColors();
    }
  };

  const handleDeleteColor = async (id: string) => {
    if (!confirm('Delete this color?')) return;
    await supabase.from('product_colors').delete().eq('id', id);
    fetchColors();
  };

  const handleEditColor = (color: ProductColor) => {
    setEditingColorId(color.id);
    setEditColorName(color.name);
    setEditColorHex(color.hex_code || '#000000');
  };

  const handleUpdateColor = async () => {
    if (!editColorName.trim() || !editingColorId) return;
    await supabase
      .from('product_colors')
      .update({
        name: editColorName.trim(),
        hex_code: editColorHex
      })
      .eq('id', editingColorId);
    setEditingColorId(null);
    setEditColorName('');
    setEditColorHex('#000000');
    fetchColors();
  };

  const handleCancelEditColor = () => {
    setEditingColorId(null);
    setEditColorName('');
    setEditColorHex('#000000');
  };

  const handleAddSize = async () => {
    if (!newSizeName.trim() || !adminId) return;
    const { error } = await supabase
      .from('product_sizes')
      .insert({
        admin_id: adminId,
        name: newSizeName.trim(),
        sort_order: newSizeOrder
      });
    if (!error) {
      setNewSizeName('');
      setNewSizeOrder(sizes.length);
      fetchSizes();
    }
  };

  const handleDeleteSize = async (id: string) => {
    if (!confirm('Delete this size?')) return;
    await supabase.from('product_sizes').delete().eq('id', id);
    fetchSizes();
  };

  const fetchLocations = async () => {
    if (!adminId) return;
    const { data } = await supabase
      .from('product_locations')
      .select('*')
      .eq('admin_id', adminId)
      .order('name');
    if (data) setLocations(data);
  };

  const fetchProductCategories = async () => {
    if (!adminId) return;
    const { data } = await supabase
      .from('product_categories')
      .select('*')
      .eq('admin_id', adminId)
      .order('name');
    if (data) setProductCategories(data);
  };

  const handleAddLocation = async () => {
    if (!newLocationName.trim() || !adminId) return;
    const { error } = await supabase
      .from('product_locations')
      .insert({
        admin_id: adminId,
        name: newLocationName.trim()
      });
    if (!error) {
      setNewLocationName('');
      fetchLocations();
    }
  };

  const handleUpdateLocation = async (id: string) => {
    if (!editLocationName.trim()) return;
    const { error } = await supabase
      .from('product_locations')
      .update({ name: editLocationName.trim() })
      .eq('id', id);
    if (!error) {
      setEditingLocationId(null);
      setEditLocationName('');
      fetchLocations();
    }
  };

  const handleDeleteLocation = async (id: string) => {
    if (!confirm('Delete this location?')) return;
    await supabase.from('product_locations').delete().eq('id', id);
    fetchLocations();
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim() || !adminId) return;
    const { error } = await supabase
      .from('product_categories')
      .insert({
        admin_id: adminId,
        name: newCategoryName.trim()
      });
    if (!error) {
      setNewCategoryName('');
      fetchProductCategories();
    }
  };

  const handleUpdateCategory = async (id: string) => {
    if (!editCategoryName.trim() || !adminId) return;

    const oldCategory = productCategories.find(c => c.id === id);
    if (!oldCategory) return;

    const oldName = oldCategory.name;
    const newName = editCategoryName.trim();

    if (oldName === newName) {
      setEditingCategoryId(null);
      setEditCategoryName('');
      return;
    }

    const existingCategory = productCategories.find(
      c => c.id !== id && c.name.toLowerCase() === newName.toLowerCase()
    );
    if (existingCategory) {
      alert(`Category "${newName}" already exists`);
      return;
    }

    const { error: productsError } = await supabase
      .from('products')
      .update({ category: newName })
      .eq('admin_id', adminId)
      .eq('category', oldName);

    if (productsError) {
      console.error('Failed to update products:', productsError);
      alert('Failed to update products with new category name');
      return;
    }

    const { error: categoryError } = await supabase
      .from('product_categories')
      .update({ name: newName })
      .eq('id', id);

    if (categoryError) {
      console.error('Failed to update category:', categoryError);
      await supabase
        .from('products')
        .update({ category: oldName })
        .eq('admin_id', adminId)
        .eq('category', newName);
      alert('Failed to update category name');
      return;
    }

    setEditingCategoryId(null);
    setEditCategoryName('');
    fetchProductCategories();
    await refreshInventory();
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Delete this category?')) return;
    await supabase.from('product_categories').delete().eq('id', id);
    fetchProductCategories();
  };

  const canModify = user?.role !== 'viewer';
  const hasPricingAccess = user?.pricing_enabled || user?.role === 'super_admin';

  const categoriesFromItems = allItems.map(item => item.category).filter(Boolean) as string[];
  const categoriesFromDatabase = productCategories.map(c => c.name);
  const categories = [...new Set([...categoriesFromDatabase, ...categoriesFromItems])].sort();

  const locationsFromItems = items.map(item => item.location).filter(Boolean) as string[];
  const locationsFromDatabase = locations.map(l => l.name);
  const allLocations = [...new Set([...locationsFromDatabase, ...locationsFromItems])].sort();

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-40" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 ml-1" />
      : <ArrowDown className="h-3.5 w-3.5 ml-1" />;
  };

  const getColorName = (colorId: string | undefined) => {
    if (!colorId) return '';
    return colors.find(c => c.id === colorId)?.name || '';
  };

  const getSizeName = (sizeId: string | undefined) => {
    if (!sizeId) return '';
    return sizes.find(s => s.id === sizeId)?.name || '';
  };

  const getSizeSortOrder = (sizeId: string | undefined) => {
    if (!sizeId) return 9999;
    return sizes.find(s => s.id === sizeId)?.sort_order ?? 9999;
  };

  const getColorHex = (colorId: string | undefined) => {
    if (!colorId) return undefined;
    return colors.find(c => c.id === colorId)?.hex_code;
  };

  const matchesStockFilter = (quantity: number, filter: typeof stockFilter) => {
    if (filter === 'all') return true;
    if (filter === 'out_of_stock') return quantity === 0;
    if (filter === 'low_stock') return quantity > 0 && quantity <= 10;
    if (filter === 'in_stock') return quantity > 10;
    return true;
  };

  const filteredVariantItems = useMemo(() => {
    const source = allItems.length > 0 ? allItems : items;
    const term = searchTerm.trim().toLowerCase();

    const filtered = source.filter(item => {
      if (selectedCategory && item.category !== selectedCategory) return false;
      if (selectedColor && item.color_id !== selectedColor) return false;
      if (selectedSize && item.size_id !== selectedSize) return false;
      if (!matchesStockFilter(item.quantity, stockFilter)) return false;
      if (term) {
        const searchable = [item.name, item.sku, item.barcode, item.reference_code]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(term)) return false;
      }
      return true;
    });

    return filtered.sort((a, b) => {
      const nameCmp = a.name.localeCompare(b.name);
      if (nameCmp !== 0) return nameCmp;
      const colorCmp = getColorName(a.color_id).localeCompare(getColorName(b.color_id));
      if (colorCmp !== 0) return colorCmp;
      return getSizeSortOrder(a.size_id) - getSizeSortOrder(b.size_id);
    });
  }, [allItems, items, searchTerm, selectedCategory, selectedColor, selectedSize, stockFilter, colors, sizes]);

  const handleToggleVariantView = () => {
    if (viewMode === 'table') {
      setViewMode('variants');
      fetchAllItems(true);
    } else {
      setViewMode('table');
    }
  };

  const activeFiltersCount = [
    selectedCategory,
    selectedLocation,
    selectedColor,
    selectedSize,
    stockFilter !== 'all' ? stockFilter : ''
  ].filter(Boolean).length;

  const clearAllFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedLocation('');
    setSelectedColor('');
    setSelectedSize('');
    setStockFilter('all');
    setSortColumn(null);
    loadPage(1, pagination.pageSize, {});
  }, [loadPage, pagination.pageSize]);

  const handlePageChange = useCallback((newPage: number) => {
    const filters: InventoryFilters = {
      searchTerm: searchTerm || undefined,
      category: selectedCategory || undefined,
      colorId: selectedColor || undefined,
      sizeId: selectedSize || undefined,
      stockFilter: stockFilter,
      sortColumn: sortColumn || undefined,
      sortDirection: sortDirection
    };
    loadPage(newPage, pagination.pageSize, filters);
  }, [searchTerm, selectedCategory, selectedColor, selectedSize, stockFilter, sortColumn, sortDirection, loadPage, pagination.pageSize]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    const filters: InventoryFilters = {
      searchTerm: searchTerm || undefined,
      category: selectedCategory || undefined,
      colorId: selectedColor || undefined,
      sizeId: selectedSize || undefined,
      stockFilter: stockFilter,
      sortColumn: sortColumn || undefined,
      sortDirection: sortDirection
    };
    loadPage(1, newPageSize, filters);
  }, [searchTerm, selectedCategory, selectedColor, selectedSize, stockFilter, sortColumn, sortDirection, loadPage]);

  const { currentPage, pageSize: itemsPerPage, totalItems, totalPages } = pagination;

  const resetForm = () => {
    setEditingItem(null);
    setShowAddForm(false);
  };

  const handleFormSubmit = async (itemData: any): Promise<boolean | string> => {
    if (editingItem) {
      const success = await updateItem(editingItem.id, itemData);
      if (success) {
        alert(t('common.updated'));
        return true;
      } else {
        alert(t('common.failed'));
        return false;
      }
    } else {
      const newItem = await addItem(itemData);
      if (newItem) {
        alert(t('common.added'));
        return newItem.id;
      } else {
        alert(t('common.failed'));
        return false;
      }
    }
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setShowAddForm(true);
  };

  const handleDelete = async (itemId: string) => {
    if (!confirm(t('common.confirmDelete'))) return;

    const result = await deleteItem(itemId);

    if (result.success) {
      success(t('common.deleted'));
      return;
    }

    // Sales, invoices or shipments still reference the product. Offer to merge
    // it into the one being kept instead of reporting an unexplained failure.
    if (result.blockers?.length) {
      const item = allItems.find(candidate => candidate.id === itemId)
        ?? items.find(candidate => candidate.id === itemId);

      if (item) {
        setBlockedDeletion({
          item: { id: item.id, name: item.name, sku: item.sku, quantity: item.quantity },
          blockers: result.blockers,
        });
        return;
      }
    }

    error(result.reason ?? t('common.failed'));
  };

  const handleDuplicate = async (item: any) => {
    try {
      // Generate unique name for duplicate
      const baseName = item.name.replace(/ \(copy( \d+)?\)$/, '').replace(/ x\d+$/, '');

      // Find all items with similar names to determine copy number
      const existingCopies = items.filter(i => {
        const itemBaseName = i.name.replace(/ \(copy( \d+)?\)$/, '').replace(/ x\d+$/, '');
        return itemBaseName === baseName && i.id !== item.id;
      });

      // Generate unique name
      let newName: string;
      if (existingCopies.length === 0) {
        newName = `${baseName} (copy)`;
      } else {
        newName = `${baseName} (copy ${existingCopies.length + 1})`;
      }

      // Create duplicate with only the essential fields
      // Explicitly exclude SKU, barcode, and batch to force new unique values
      const duplicateData = {
        name: newName,
        category: item.category,
        quantity: item.quantity,
        location: item.location || undefined,
        description: item.description || undefined,
        buy_price: item.buy_price || undefined,
        sell_price: item.sell_price || undefined,
        danger_category: item.danger_category || undefined,
        image_url: item.image_url || undefined,
        color_id: item.color_id || undefined,
        size_id: item.size_id || undefined,
        supplier: item.supplier || undefined,
        minimum_stock: item.minimum_stock || undefined,
        maximum_stock: item.maximum_stock || undefined,
        reorder_point: item.reorder_point || undefined,
        status: item.status || 'active',
        // Do NOT include: sku, barcode, batch, batch_number
        // These will be auto-generated to ensure uniqueness
      };

      const result = await addItem(duplicateData);

      if (result) {
        alert(`✓ Item duplicated successfully as "${newName}"`);
      } else {
        alert(t('common.failed') + ': Unable to create duplicate item');
      }
    } catch (error) {
      console.error('Error duplicating item:', error);
      alert(t('common.failed') + ': ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleBarcodeScanned = (barcode: string) => {
    setEditingItem({ batch: barcode, name: `Item ${barcode}` });
    setShowAddForm(true);
  };

  const handleDuplicateWithSizes = async () => {
    if (!selectedItemForDuplicate || !adminId || selectedSizesForDuplicate.size === 0) return;

    const selectedSizes = allStandardSizes.filter(s => selectedSizesForDuplicate.has(s));
    const item = selectedItemForDuplicate;
    const totalSizes = selectedSizes.length;

    setDuplicateSizeProgress({ current: 0, total: totalSizes, status: 'Preparing sizes...' });

    try {
      const sizeIds: { id: string; name: string }[] = [];
      for (let i = 0; i < selectedSizes.length; i++) {
        const sizeName = selectedSizes[i];
        setDuplicateSizeProgress({ current: i, total: totalSizes, status: `Checking size ${sizeName}...` });

        let existingSize = sizes.find(s => s.name.toUpperCase() === sizeName.toUpperCase());

        if (!existingSize) {
          const sortOrder = allStandardSizes.indexOf(sizeName) + 1;
          const { data: newSize, error } = await supabase
            .from('product_sizes')
            .insert({
              admin_id: adminId,
              name: sizeName,
              sort_order: sortOrder
            })
            .select()
            .single();

          if (error) {
            console.error(`Failed to create size ${sizeName}:`, error);
            continue;
          }
          existingSize = newSize;
        }

        if (existingSize) {
          sizeIds.push({ id: existingSize.id, name: sizeName });
        }
      }

      await fetchSizes();

      let successCount = 0;
      for (let i = 0; i < sizeIds.length; i++) {
        const { id: sizeId, name: sizeName } = sizeIds[i];

        setDuplicateSizeProgress({
          current: i + 1,
          total: totalSizes,
          status: `Creating ${item.name} - Size ${sizeName}...`
        });

        const duplicateData = {
          name: item.name,
          category: item.category,
          quantity: item.quantity,
          location: item.location || undefined,
          description: item.description || undefined,
          buy_price: item.buy_price || undefined,
          sell_price: item.sell_price || undefined,
          danger_category: item.danger_category || undefined,
          image_url: item.image_url || undefined,
          color_id: item.color_id || undefined,
          size_id: sizeId,
          supplier: item.supplier || undefined,
          minimum_stock: item.minimum_stock || undefined,
          maximum_stock: item.maximum_stock || undefined,
          reorder_point: item.reorder_point || undefined,
          status: item.status || 'active',
        };

        const result = await addItem(duplicateData);
        if (result) {
          successCount++;
        }

        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setDuplicateSizeProgress(null);
      setShowDuplicateSizeModal(false);
      setSelectedItemForDuplicate(null);
      setDuplicateSizeSearch('');
      setSelectedSizesForDuplicate(new Set(allStandardSizes));

      const sizeList = selectedSizes.join(', ');
      if (successCount === totalSizes) {
        alert(`Successfully created ${totalSizes} size variant${totalSizes > 1 ? 's' : ''} of "${item.name}" (${sizeList})`);
      } else {
        alert(`Created ${successCount} of ${totalSizes} size variants. Some may have failed.`);
      }
    } catch (error) {
      console.error('Error duplicating with sizes:', error);
      setDuplicateSizeProgress(null);
      alert('Failed to create size variants: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const stats = {
    totalItems: totalStats.totalItems,
    totalQuantity: totalStats.totalQuantity || items.reduce((sum, item) => sum + item.quantity, 0),
    lowStock: totalStats.lowStock,
    outOfStock: totalStats.outOfStock,
    categories: categories.length,
    totalValue: hasPricingAccess ? items.reduce((sum, item) => sum + (item.quantity * (item.sell_price || 0)), 0) : 0
  };

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <Package className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('inventory.title')}
            </h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 ml-[52px]">
            {totalItems === stats.totalItems
              ? `${stats.totalItems} items in inventory`
              : `Showing ${totalItems} filtered items of ${stats.totalItems} total`
            }
          </p>
        </div>

        {canModify && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <button
                onClick={() => setShowLocationModal(true)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-white dark:hover:bg-gray-700 rounded-md transition-colors"
                title="Manage Locations"
              >
                <MapPin className="h-4 w-4" />
              </button>
              <button
                onClick={() => setShowCategoryModal(true)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-white dark:hover:bg-gray-700 rounded-md transition-colors"
                title="Manage Categories"
              >
                <Tag className="h-4 w-4" />
              </button>
              <button
                onClick={() => setShowColorModal(true)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white dark:hover:bg-gray-700 rounded-md transition-colors"
                title="Manage Colors"
              >
                <Palette className="h-4 w-4" />
              </button>
              <button
                onClick={() => setShowSizeModal(true)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-white dark:hover:bg-gray-700 rounded-md transition-colors"
                title="Manage Sizes"
              >
                <Ruler className="h-4 w-4" />
              </button>
            </div>
            <Button
              onClick={() => {
                setShowDuplicateSizeModal(true);
                setSelectedItemForDuplicate(null);
                setDuplicateSizeSearch('');
              }}
              variant="secondary"
              className="text-sm"
            >
              <Layers className="h-4 w-4 mr-1.5" />
              Duplicate Sizes
            </Button>
            <Button
              onClick={handleRepairWarehouse}
              loading={repairingWarehouse}
              variant="secondary"
              className="text-sm"
              title="Create missing Warehouse rows for products so they appear in the B2B Portal"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Repair Warehouse
            </Button>
            {shopConnected && Array.from(shopifyStatus.values()).some(status => status === 'matchable') && (
              <Button
                onClick={handleLinkAllMatching}
                loading={linkingAll}
                variant="secondary"
                className="text-sm border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30"
              >
                <Link2 className="h-4 w-4 mr-1.5" />
                Link All ({Array.from(shopifyStatus.values()).filter(status => status === 'matchable').length})
              </Button>
            )}
            {shopConnected && (
              <Button
                onClick={handleRefreshShopifyQty}
                loading={refreshingQty}
                variant="secondary"
                className="text-sm"
                title="Fetch the current Shopify quantity for linked products so the comparison icons are up to date"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" />
                Refresh Shopify qty
              </Button>
            )}
            <Button
              onClick={() => !isNetworkOffline && setShowAddForm(true)}
              variant="primary"
              disabled={isNetworkOffline}
              title={isNetworkOffline ? 'Cannot add items while offline' : undefined}
              className="bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 border-0 shadow-lg shadow-emerald-500/25"
            >
              {isNetworkOffline ? <WifiOff className="h-4 w-4 mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
              {t('inventory.addItem')}
            </Button>
          </div>
        )}
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <button
          onClick={() => { clearAllFilters(); }}
          className="card p-4 hover:shadow-lg transition-all duration-200 group cursor-pointer border-2 border-transparent hover:border-blue-200 dark:hover:border-blue-800"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalItems}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('dashboard.totalItems')}</div>
        </button>

        <button
          onClick={() => { clearAllFilters(); setStockFilter('in_stock'); }}
          className="card p-4 hover:shadow-lg transition-all duration-200 group cursor-pointer border-2 border-transparent hover:border-emerald-200 dark:hover:border-emerald-800"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalQuantity.toLocaleString()}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('dashboard.totalQuantity')}</div>
        </button>

        <button
          onClick={() => { clearAllFilters(); setStockFilter('low_stock'); }}
          className="card p-4 hover:shadow-lg transition-all duration-200 group cursor-pointer border-2 border-transparent hover:border-amber-200 dark:hover:border-amber-800"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            {stats.lowStock > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-full">
                {stats.lowStock}
              </span>
            )}
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.lowStock}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('dashboard.lowStock')}</div>
        </button>

        <button
          onClick={() => { clearAllFilters(); setStockFilter('out_of_stock'); }}
          className="card p-4 hover:shadow-lg transition-all duration-200 group cursor-pointer border-2 border-transparent hover:border-red-200 dark:hover:border-red-800"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <PackageX className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            {stats.outOfStock > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-full">
                {stats.outOfStock}
              </span>
            )}
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.outOfStock}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Out of Stock</div>
        </button>

        {hasPricingAccess ? (
          <div className="card p-4 hover:shadow-lg transition-all duration-200 group border-2 border-transparent hover:border-teal-200 dark:hover:border-teal-800">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <DollarSign className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">${stats.totalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Inventory Value</div>
          </div>
        ) : (
          <div className="card p-4 hover:shadow-lg transition-all duration-200 group border-2 border-transparent hover:border-gray-200 dark:hover:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Tag className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.categories}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('dashboard.categories')}</div>
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <div className="card mb-6 overflow-hidden">
        {/* Main Search Bar */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, batch, SKU, or barcode..."
                className="w-full pl-10 pr-10 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-blue-300 placeholder:text-gray-400 dark:placeholder:text-blue-400/60 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-blue-300 focus:ring-2 focus:ring-emerald-500 min-w-[140px]"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => viewMode !== 'table' && handleToggleVariantView()}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-all ${
                    viewMode === 'table'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title="Standard table view"
                >
                  <Package className="h-4 w-4" />
                  Table
                </button>
                <button
                  onClick={() => viewMode !== 'variants' && handleToggleVariantView()}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-all border-l border-gray-200 dark:border-gray-700 ${
                    viewMode === 'variants'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title="Group products by name, color, and size"
                >
                  <Layers className="h-4 w-4" />
                  Group variants
                </button>
              </div>

              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  showAdvancedFilters || activeFiltersCount > 0
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                    : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span className="hidden sm:inline">Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="w-5 h-5 flex items-center justify-center bg-emerald-600 text-white text-xs font-bold rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
                {showAdvancedFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>

              <div className="hidden sm:flex items-center gap-1 border-l border-gray-200 dark:border-gray-700 pl-2">
                <button
                  onClick={() => setShowScanner(true)}
                  className="p-2.5 text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  title="Camera Scanner"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setShowExternalScanner(true)}
                  className="p-2.5 text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  title="External Scanner"
                >
                  <Zap className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-blue-400 mb-1.5">Location</label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-blue-300 focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">All Locations</option>
                  {allLocations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-blue-400 mb-1.5">Color</label>
                <select
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-blue-300 focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">All Colors</option>
                  {colors.map(color => (
                    <option key={color.id} value={color.id}>{color.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-blue-400 mb-1.5">Size</label>
                <select
                  value={selectedSize}
                  onChange={(e) => setSelectedSize(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-blue-300 focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">All Sizes</option>
                  {sizes.map(size => (
                    <option key={size.id} value={size.id}>{size.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-blue-400 mb-1.5">Stock Status</label>
                <select
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value as any)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-blue-300 focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="all">All Status</option>
                  <option value="in_stock">In Stock (&gt;10)</option>
                  <option value="low_stock">Low Stock (1-10)</option>
                  <option value="out_of_stock">Out of Stock (0)</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearAllFilters}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-blue-300 hover:text-red-600 dark:hover:text-red-400 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-red-200 dark:hover:border-red-800 transition-colors"
                >
                  <RotateCcw className="h-4 w-4" />
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active Filter Badges */}
        {(activeFiltersCount > 0 || searchTerm) && (
          <div className="px-4 py-2.5 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Active filters:</span>
            {searchTerm && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                Search: "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="hover:text-blue-900 dark:hover:text-blue-100">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedCategory && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-medium">
                Category: {selectedCategory}
                <button onClick={() => setSelectedCategory('')} className="hover:text-emerald-900 dark:hover:text-emerald-100">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedLocation && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-full text-xs font-medium">
                Location: {selectedLocation}
                <button onClick={() => setSelectedLocation('')} className="hover:text-teal-900 dark:hover:text-teal-100">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedColor && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                Color: {colors.find(c => c.id === selectedColor)?.name}
                <button onClick={() => setSelectedColor('')} className="hover:text-blue-900 dark:hover:text-blue-100">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedSize && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">
                Size: {sizes.find(s => s.id === selectedSize)?.name}
                <button onClick={() => setSelectedSize('')} className="hover:text-gray-900 dark:hover:text-gray-100">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {stockFilter !== 'all' && (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                stockFilter === 'out_of_stock' ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                stockFilter === 'low_stock' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' :
                'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              }`}>
                Stock: {stockFilter.replace('_', ' ')}
                <button onClick={() => setStockFilter('all')} className="hover:opacity-70">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Items Table / Variant List */}
      <div className="card overflow-hidden">
        {viewMode === 'variants' ? (
          <div className="max-h-[calc(100vh-320px)] min-h-[400px] overflow-y-auto">
            {allItemsLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-10 w-10 animate-spin text-teal-600 mb-4" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading all variants...</p>
              </div>
            ) : filteredVariantItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6">
                <Package className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No variants found</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {searchTerm || activeFiltersCount > 0
                    ? 'Try adjusting your search or filters'
                    : 'Add products to see grouped variants'}
                </p>
                {(searchTerm || activeFiltersCount > 0) && (
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              <div>
                {filteredVariantItems.map((item, index) => {
                  const prev = index > 0 ? filteredVariantItems[index - 1] : null;
                  const showGroupHeader = !prev || prev.name !== item.name || prev.color_id !== item.color_id;
                  const colorName = getColorName(item.color_id);
                  const colorHex = getColorHex(item.color_id);
                  const sizeName = getSizeName(item.size_id);

                  return (
                    <React.Fragment key={item.id}>
                      {showGroupHeader && (
                        <div className="sticky top-0 z-10 px-4 py-2 bg-gray-100/95 dark:bg-gray-800/95 backdrop-blur border-y border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                              {item.name}
                            </span>
                            {colorName && (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                                {colorHex && (
                                  <span
                                    className="w-2.5 h-2.5 rounded-full border border-gray-300 dark:border-gray-600"
                                    style={{ backgroundColor: colorHex }}
                                  />
                                )}
                                {colorName}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      <div
                        className={`flex items-center justify-between gap-4 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                          item.quantity === 0 ? 'bg-red-50/40 dark:bg-red-900/10' : item.quantity <= 10 ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
                          <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                            {item.name}
                          </span>
                          {colorName && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              {colorHex && (
                                <span
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: colorHex }}
                                />
                              )}
                              {colorName}
                            </span>
                          )}
                          {sizeName && (
                            <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                              {sizeName}
                            </span>
                          )}
                          {item.sku && (
                            <span className="px-1.5 py-0.5 rounded text-xs font-mono bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
                              {item.sku}
                            </span>
                          )}
                          {item.category && (
                            <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              {item.category}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {hasPricingAccess && item.sell_price != null && (
                            <span className="text-sm font-medium text-green-600 dark:text-green-400">
                              {'\u20AC'}{item.sell_price.toFixed(2)}
                            </span>
                          )}
                          <span className={`text-sm font-bold tabular-nums ${
                            item.quantity === 0 ? 'text-red-600 dark:text-red-400' :
                            item.quantity <= 10 ? 'text-amber-600 dark:text-amber-400' :
                            'text-gray-700 dark:text-gray-300'
                          }`}>
                            {item.quantity}
                          </span>
                          {canModify && (
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
            {!allItemsLoading && filteredVariantItems.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 text-sm text-gray-600 dark:text-gray-400">
                Showing <span className="font-medium text-gray-900 dark:text-gray-100">{filteredVariantItems.length}</span> variants
              </div>
            )}
          </div>
        ) : (
        <>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th
                  onClick={() => handleSort('name')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
                >
                  <div className="flex items-center">
                    {t('inventory.table.name')}
                    {getSortIcon('name')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('sku')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
                >
                  <div className="flex items-center">
                    SKU
                    {getSortIcon('sku')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('reference_code')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
                >
                  <div className="flex items-center">
                    Reference
                    {getSortIcon('reference_code')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('color')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
                >
                  <div className="flex items-center">
                    Color
                    {getSortIcon('color')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('size')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
                >
                  <div className="flex items-center">
                    Size
                    {getSortIcon('size')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('category')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
                >
                  <div className="flex items-center">
                    {t('inventory.table.category')}
                    {getSortIcon('category')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('quantity')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
                >
                  <div className="flex items-center">
                    {t('inventory.table.quantity')}
                    {getSortIcon('quantity')}
                  </div>
                </th>
                {hasPricingAccess && (
                  <>
                    <th
                      onClick={() => handleSort('buy_price')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
                    >
                      <div className="flex items-center">
                        {t('common.buyPrice')}
                        {getSortIcon('buy_price')}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('sell_price')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
                    >
                      <div className="flex items-center">
                        {t('common.sellPrice')}
                        {getSortIcon('sell_price')}
                      </div>
                    </th>
                  </>
                )}
                <th
                  onClick={() => handleSort('location')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
                >
                  <div className="flex items-center">
                    {t('inventory.table.location')}
                    {getSortIcon('location')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Box className="h-3.5 w-3.5" />
                    Bin Location
                  </div>
                </th>
                {canModify && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('inventory.table.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={canModify ? (hasPricingAccess ? 11 : 9) : (hasPricingAccess ? 10 : 8)} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                        <Package className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No items found</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        {searchTerm || activeFiltersCount > 0
                          ? "Try adjusting your search or filters"
                          : "Add your first inventory item to get started"
                        }
                      </p>
                      {(searchTerm || activeFiltersCount > 0) && (
                        <button
                          onClick={clearAllFilters}
                          className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : items.map((item) => (
                <tr key={item.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${item.quantity === 0 ? 'bg-red-50/50 dark:bg-red-900/10' : item.quantity <= 10 ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {item.image_url ? (
                        <button
                          onClick={() => setPreviewImage({ url: item.image_url!, name: item.name })}
                          className="group relative focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-lg"
                        >
                          <img src={item.image_url} alt={item.name} crossOrigin="anonymous" className="w-10 h-10 rounded-lg object-contain bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-transform group-hover:scale-105" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors flex items-center justify-center">
                            <Search className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </button>
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          <Package className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {item.name}
                          </span>
                          {item.quantity === 0 && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded">Out</span>
                          )}
                          {item.quantity > 0 && item.quantity <= 10 && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded">Low</span>
                          )}
                        </div>
                        {item.batch && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                            {item.batch}
                          </div>
                        )}
                        {item.danger_category && (
                          <div className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {item.danger_category}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {item.sku ? (
                        <span className="text-sm font-mono text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 px-2 py-0.5 rounded">
                          {item.sku}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                      {shopConnected && (
                        <>
                          {(() => {
                            const locComp = shopifyLocComparison.get(item.id);
                            if (locComp && locComp.length > 0) {
                              return (
                                <span className="inline-flex items-center gap-1.5">
                                  {locComp.map((loc) => {
                                    const letter = loc.locationName.charAt(0).toUpperCase();
                                    if (loc.shopifyQty == null) {
                                      return (
                                        <span
                                          key={loc.shopifyLocationId}
                                          title={`${loc.locationName}: Shopify quantity not verified yet (auto-refreshes nightly)`}
                                          className="inline-flex items-center gap-0.5 text-[11px] font-medium text-amber-500"
                                        >
                                          <span className="text-gray-400 dark:text-gray-500">{letter}</span>
                                          <Cloud className="h-3.5 w-3.5" />
                                        </span>
                                      );
                                    }
                                    const diff = loc.shopifyQty - loc.localQty;
                                    if (diff === 0) {
                                      return (
                                        <span
                                          key={loc.shopifyLocationId}
                                          title={`${loc.locationName} in sync (${loc.shopifyQty})`}
                                          className="inline-flex items-center gap-0.5 text-[11px] font-medium text-emerald-500"
                                        >
                                          <span className="text-gray-400 dark:text-gray-500">{letter}</span>
                                          <Check className="h-3.5 w-3.5" />
                                        </span>
                                      );
                                    }
                                    if (diff > 0) {
                                      return (
                                        <span
                                          key={loc.shopifyLocationId}
                                          title={`${loc.locationName}: Shopify has ${diff} more (local ${loc.localQty}, Shopify ${loc.shopifyQty})`}
                                          className="inline-flex items-center gap-0.5 text-[11px] font-medium text-red-500"
                                        >
                                          <span className="text-gray-400 dark:text-gray-500">{letter}</span>
                                          <ArrowUp className="h-3.5 w-3.5" />+{diff}
                                        </span>
                                      );
                                    }
                                    return (
                                      <span
                                        key={loc.shopifyLocationId}
                                        title={`${loc.locationName}: Shopify has ${Math.abs(diff)} fewer (local ${loc.localQty}, Shopify ${loc.shopifyQty})`}
                                        className="inline-flex items-center gap-0.5 text-[11px] font-medium text-amber-600"
                                      >
                                        <span className="text-gray-400 dark:text-gray-500">{letter}</span>
                                        <ArrowDown className="h-3.5 w-3.5" />{diff}
                                      </span>
                                    );
                                  })}
                                </span>
                              );
                            }
                            const mapping = shopifyMappings.get(item.id);
                            if (mapping) {
                              return (
                                <span title="Linked to Shopify - awaiting quantity check" className="text-emerald-500">
                                  <Check className="h-3.5 w-3.5" />
                                </span>
                              );
                            }
                            const linkStatus = shopifyStatus.get(item.id);
                            if (linkStatus === 'matchable') {
                              return (
                                <span title="SKU found in Shopify - click sync to link" className="text-blue-500">
                                  <Link2 className="h-3.5 w-3.5" />
                                </span>
                              );
                            }
                            if (linkStatus === 'tracking_off') {
                              return (
                                <span title="Tracking is OFF in Shopify - enable Track quantity to link" className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                                  Tracking off
                                </span>
                              );
                            }
                            if (linkStatus === 'not_found') {
                              return (
                                <span title="Not in Shopify (no longer exists)" className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                  Not in Shopify
                                </span>
                              );
                            }
                            if (discoveringSkus && item.sku && !item.sku.startsWith('SHOP-')) {
                              return (
                                <span title="Checking Shopify..." className="text-gray-400">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                </span>
                              );
                            }
                            return (
                              <span title="Not found in Shopify" className="text-gray-300 dark:text-gray-600">
                                <CloudOff className="h-3.5 w-3.5" />
                              </span>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {item.reference_code ? (
                      <span className="text-sm font-mono text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 px-2 py-0.5 rounded">
                        {item.reference_code}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {item.color_id ? (
                      <div className="flex items-center gap-2">
                        {(() => {
                          const color = colors.find(c => c.id === item.color_id);
                          return color ? (
                            <>
                              <div
                                className="w-5 h-5 rounded-md border border-gray-200 dark:border-gray-600 shadow-sm"
                                style={{ backgroundColor: color.hex_code || '#ccc' }}
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{color.name}</span>
                            </>
                          ) : <span className="text-sm text-gray-400">-</span>;
                        })()}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {item.size_id ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                        {sizes.find(s => s.id === item.size_id)?.name || '-'}
                      </span>
                    ) : <span className="text-sm text-gray-400">-</span>}
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-sm font-medium ${
                      item.quantity === 0 
                        ? 'text-red-600 dark:text-red-400'
                        : item.quantity <= 10
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}>
                      {item.quantity}
                    </span>
                  </td>
                  {hasPricingAccess && (
                    <>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {item.buy_price ? `€${item.buy_price.toFixed(2)}` : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {item.sell_price ? `€${item.sell_price.toFixed(2)}` : '-'}
                      </td>
                    </>
                  )}
                  <td className="px-6 py-4">
                    {(() => {
                      const quantities = locationQuantitiesMap.get(item.id) || [];
                      if (quantities.length > 0) {
                        return <LocationQuantityDisplay quantities={quantities} />;
                      }
                      if (item.location) {
                        return (
                          <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                            <MapPin className="h-3.5 w-3.5" />
                            {item.location}
                          </span>
                        );
                      }
                      return <span className="text-sm text-gray-400">-</span>;
                    })()}
                  </td>
                  <td className="px-6 py-4">
                    <BinLocationDisplay binLocations={binLocationsMap} productId={item.id} />
                  </td>
                  {canModify && (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {shopConnected && item.sku && !item.sku.startsWith('SHOP-') && (
                          (() => {
                            const isMapped = shopifyMappings.has(item.id);
                            const linkStatus = shopifyStatus.get(item.id);
                            const isMatchable = linkStatus === 'matchable';
                            const needsSync = isMapped && shopifyMappings.get(item.id)?.lastPushedQty !== item.quantity;
                            const showButton = isMapped || isMatchable;
                            if (!showButton) return null;
                            return (
                              <button
                                onClick={() => syncProductToShopify(item.id)}
                                disabled={syncingProductId === item.id}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  syncingProductId === item.id
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : isMapped
                                    ? needsSync
                                      ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                                      : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                                    : 'text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-700'
                                }`}
                                title={syncingProductId === item.id
                                  ? 'Syncing...'
                                  : isMapped
                                  ? needsSync ? 'Push updated quantity to Shopify' : 'Re-sync to Shopify'
                                  : 'Link & sync to Shopify'}
                              >
                                {syncingProductId === item.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : isMapped ? (
                                  <RefreshCw className="h-4 w-4" />
                                ) : (
                                  <Upload className="h-4 w-4" />
                                )}
                              </button>
                            );
                          })()
                        )}
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(item)}
                          className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-colors"
                          title="Duplicate"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <span>
                  {inventoryLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </span>
                  ) : (
                    <>
                      Showing <span className="font-medium text-gray-900 dark:text-gray-100">{Math.min(((currentPage - 1) * itemsPerPage) + 1, totalItems)}</span> to{' '}
                      <span className="font-medium text-gray-900 dark:text-gray-100">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                      <span className="font-medium text-gray-900 dark:text-gray-100">{totalItems}</span> items
                    </>
                  )}
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || inventoryLoading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="First page"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <ChevronLeft className="h-4 w-4 -ml-2" />
                </button>
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || inventoryLoading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => {
                    let pageNum;
                    const tp = totalPages || 1;
                    if (tp <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= tp - 2) {
                      pageNum = tp - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    if (pageNum < 1 || pageNum > tp) return null;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={inventoryLoading}
                        className={`w-8 h-8 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? 'bg-emerald-600 text-white'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(Math.min(totalPages || 1, currentPage + 1))}
                  disabled={currentPage === totalPages || inventoryLoading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handlePageChange(totalPages || 1)}
                  disabled={currentPage === totalPages || inventoryLoading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Last page"
                >
                  <ChevronRight className="h-4 w-4" />
                  <ChevronRight className="h-4 w-4 -ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* Add/Edit Item Modal */}
      <AddItemForm
        isOpen={showAddForm}
        onClose={resetForm}
        onSubmit={handleFormSubmit}
        editingItem={editingItem}
        colors={colors}
        sizes={sizes}
        locations={locations}
        categories={categories}
        adminId={adminId || ''}
        hasPricingAccess={hasPricingAccess}
        isAdmin={isAdmin}
        onLocationQuantitiesChanged={fetchLocationQuantities}
        onBinAssignmentChanged={fetchBinLocations}
      />

      {/* Barcode Scanner */}
      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScanned}
        title={t('inventory.scanBarcodeTitle')}
      />

      {/* Auto Scanner */}
      <ExternalScanner
        isOpen={showExternalScanner}
        onClose={() => setShowExternalScanner(false)}
        onScan={(barcode, item) => {
          if (item) {
            handleEdit(item);
          } else {
            setEditingItem({ batch: barcode, name: `Item ${barcode}` });
            setShowAddForm(true);
          }
        }}
        mode="search"
        title="Auto Scanner - Inventory"
      />

      {blockedDeletion && (
        <MergeDuplicateModal
          product={blockedDeletion.item}
          blockers={blockedDeletion.blockers}
          candidates={allItems.length > 0 ? allItems : items}
          onClose={() => setBlockedDeletion(null)}
          onMerged={async ({ movedSalesRecords, movedHistoricalRows }) => {
            setBlockedDeletion(null);
            success(
              `Duplicate merged. Moved ${movedSalesRecords} sales records and ${movedHistoricalRows} other historical rows.`,
            );
            await refreshInventory();
          }}
        />
      )}

      {showColorModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border max-w-lg shadow-xl rounded-xl bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 flex items-center">
                <Palette className="h-5 w-5 mr-2 text-blue-500" />
                Manage Colors
              </h3>
              <button
                onClick={() => setShowColorModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  label="Color Name"
                  value={newColorName}
                  onChange={(e) => setNewColorName(e.target.value)}
                  placeholder="e.g., Navy Blue"
                  className="flex-1"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Color
                  </label>
                  <input
                    type="color"
                    value={newColorHex}
                    onChange={(e) => setNewColorHex(e.target.value)}
                    className="h-10 w-16 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={handleAddColor} variant="primary">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Existing Colors ({colors.length})
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {colors.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No colors added yet</p>
                  ) : (
                    colors.map(color => (
                      <div
                        key={color.id}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600"
                            style={{ backgroundColor: color.hex_code || '#gray' }}
                          />
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {color.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditColor(color)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteColor(color.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={() => setShowColorModal(false)} variant="secondary">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {editingColorId && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleCancelEditColor();
          }}
        >
          <div className="relative top-20 mx-auto p-6 border max-w-md shadow-xl rounded-xl bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 flex items-center">
                <Pencil className="h-5 w-5 mr-2 text-blue-500" />
                Edit Color
              </h3>
              <button
                onClick={handleCancelEditColor}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex gap-3">
                <Input
                  label="Color Name"
                  value={editColorName}
                  onChange={(e) => setEditColorName(e.target.value)}
                  placeholder="e.g., Navy Blue"
                  className="flex-1"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') handleCancelEditColor();
                    if (e.key === 'Enter') handleUpdateColor();
                  }}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Color
                  </label>
                  <input
                    type="color"
                    value={editColorHex}
                    onChange={(e) => setEditColorHex(e.target.value)}
                    className="h-10 w-16 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') handleCancelEditColor();
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600"
                  style={{ backgroundColor: editColorHex }}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Preview: <span className="font-medium text-gray-900 dark:text-gray-100">{editColorName || 'Color Name'}</span>
                </span>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button onClick={handleCancelEditColor} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleUpdateColor} variant="primary">
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {showSizeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border max-w-lg shadow-xl rounded-xl bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 flex items-center">
                <Ruler className="h-5 w-5 mr-2 text-green-500" />
                Manage Sizes
              </h3>
              <button
                onClick={() => setShowSizeModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  label="Size Name"
                  value={newSizeName}
                  onChange={(e) => setNewSizeName(e.target.value)}
                  placeholder="e.g., XL, 42, Large"
                  className="flex-1"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Order
                  </label>
                  <input
                    type="number"
                    value={newSizeOrder}
                    onChange={(e) => setNewSizeOrder(parseInt(e.target.value) || 0)}
                    className="input w-20"
                    min="0"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={handleAddSize} variant="primary">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Existing Sizes ({sizes.length})
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {sizes.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No sizes added yet</p>
                  ) : (
                    sizes.map(size => (
                      <div
                        key={size.id}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500 dark:text-gray-400 w-8">
                            #{size.sort_order}
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {size.name}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteSize(size.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={() => setShowSizeModal(false)} variant="secondary">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {showLocationModal && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg shadow-2xl rounded-2xl bg-white dark:bg-gray-800 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-green-500 px-6 py-5">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <MapPin className="h-6 w-6 mr-2" />
                  Manage Locations
                </h3>
                <button
                  onClick={() => {
                    setShowLocationModal(false);
                    setEditingLocationId(null);
                    setEditLocationName('');
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <p className="text-emerald-100 text-sm mt-1">Add or edit storage locations for your inventory</p>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    New Location
                  </label>
                  <input
                    type="text"
                    value={newLocationName}
                    onChange={(e) => setNewLocationName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddLocation()}
                    placeholder="e.g., Warehouse A, Shelf B1"
                    className="input w-full"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleAddLocation}
                    variant="primary"
                    disabled={!newLocationName.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center justify-between">
                  <span>Existing Locations</span>
                  <span className="text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full">
                    {allLocations.length} total
                  </span>
                </h4>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {allLocations.length === 0 ? (
                    <div className="text-center py-8">
                      <MapPin className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No locations added yet</p>
                    </div>
                  ) : (
                    allLocations.map(locationName => {
                      const dbLocation = locations.find(l => l.name === locationName);
                      const isFromDatabase = !!dbLocation;

                      return (
                        <div
                          key={locationName}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg group hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                        >
                          {isFromDatabase && editingLocationId === dbLocation.id ? (
                            <div className="flex-1 flex gap-2">
                              <input
                                type="text"
                                value={editLocationName}
                                onChange={(e) => setEditLocationName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleUpdateLocation(dbLocation.id);
                                  if (e.key === 'Escape') {
                                    setEditingLocationId(null);
                                    setEditLocationName('');
                                  }
                                }}
                                className="input flex-1 text-sm py-1"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdateLocation(dbLocation.id)}
                                className="text-emerald-600 hover:text-emerald-700 p-1"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingLocationId(null);
                                  setEditLocationName('');
                                }}
                                className="text-gray-400 hover:text-gray-600 p-1"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                  <MapPin className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {locationName}
                                  </span>
                                  {!isFromDatabase && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">From items</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {isFromDatabase ? (
                                  <>
                                    <button
                                      onClick={() => {
                                        setEditingLocationId(dbLocation.id);
                                        setEditLocationName(dbLocation.name);
                                      }}
                                      className="text-gray-400 hover:text-emerald-600 p-1.5 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteLocation(dbLocation.id)}
                                      className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={async () => {
                                      if (!adminId) return;
                                      await supabase
                                        .from('product_locations')
                                        .insert({ admin_id: adminId, name: locationName });
                                      fetchLocations();
                                    }}
                                    className="text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                                  >
                                    Save
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 flex justify-end border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={() => {
                  setShowLocationModal(false);
                  setEditingLocationId(null);
                  setEditLocationName('');
                }}
                variant="secondary"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {showCategoryModal && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg shadow-2xl rounded-2xl bg-white dark:bg-gray-800 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <Tag className="h-6 w-6 mr-2" />
                  Manage Categories
                </h3>
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategoryId(null);
                    setEditCategoryName('');
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <p className="text-amber-100 text-sm mt-1">Organize your inventory with custom categories</p>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    New Category
                  </label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                    placeholder="e.g., Electronics, Clothing"
                    className="input w-full"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleAddCategory}
                    variant="primary"
                    disabled={!newCategoryName.trim()}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                {(() => {
                  const savedCategoryNames = new Set(productCategories.map(c => c.name.toLowerCase()));
                  const categoriesInUse = categories.filter(cat => !savedCategoryNames.has(cat.toLowerCase()));
                  const totalCategories = productCategories.length + categoriesInUse.length;

                  return (
                    <>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center justify-between">
                        <span>All Categories</span>
                        <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full">
                          {totalCategories} total
                        </span>
                      </h4>
                      <div className="space-y-2 max-h-72 overflow-y-auto">
                        {totalCategories === 0 ? (
                          <div className="text-center py-8">
                            <Tag className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">No categories added yet</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add categories here or assign them when creating items</p>
                          </div>
                        ) : (
                          <>
                            {productCategories.map(category => (
                              <div
                                key={category.id}
                                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg group hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                              >
                                {editingCategoryId === category.id ? (
                                  <div className="flex-1 flex gap-2">
                                    <input
                                      type="text"
                                      value={editCategoryName}
                                      onChange={(e) => setEditCategoryName(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleUpdateCategory(category.id);
                                        if (e.key === 'Escape') {
                                          setEditingCategoryId(null);
                                          setEditCategoryName('');
                                        }
                                      }}
                                      className="input flex-1 text-sm py-1"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => handleUpdateCategory(category.id)}
                                      className="text-amber-600 hover:text-amber-700 p-1"
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingCategoryId(null);
                                        setEditCategoryName('');
                                      }}
                                      className="text-gray-400 hover:text-gray-600 p-1"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                        <Tag className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                                      </div>
                                      <div>
                                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                          {category.name}
                                        </span>
                                        <span className="ml-2 text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded">
                                          Saved
                                        </span>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button
                                        onClick={() => {
                                          setEditingCategoryId(category.id);
                                          setEditCategoryName(category.name);
                                        }}
                                        className="text-gray-400 hover:text-amber-600 p-1.5 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteCategory(category.id)}
                                        className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}

                            {categoriesInUse.length > 0 && (
                              <>
                                {productCategories.length > 0 && (
                                  <div className="py-2">
                                    <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                                      <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                                      <span>From Inventory Items</span>
                                      <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                                    </div>
                                  </div>
                                )}
                                {categoriesInUse.map(categoryName => {
                                  const itemCount = items.filter(i => i.category === categoryName).length;
                                  return (
                                    <div
                                      key={categoryName}
                                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg group hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                                          <Tag className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                        </div>
                                        <div>
                                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {categoryName}
                                          </span>
                                          <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded">
                                            {itemCount} item{itemCount !== 1 ? 's' : ''}
                                          </span>
                                        </div>
                                      </div>
                                      <button
                                        onClick={async () => {
                                          if (!adminId) return;
                                          const { error } = await supabase
                                            .from('product_categories')
                                            .insert({
                                              admin_id: adminId,
                                              name: categoryName
                                            });
                                          if (!error) {
                                            fetchProductCategories();
                                          }
                                        }}
                                        className="text-xs px-2.5 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors font-medium"
                                        title="Save this category to your list"
                                      >
                                        Save
                                      </button>
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 flex justify-end border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={() => {
                  setShowCategoryModal(false);
                  setEditingCategoryId(null);
                  setEditCategoryName('');
                }}
                variant="secondary"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateSizeModal && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl shadow-2xl rounded-2xl bg-white dark:bg-gray-800 overflow-hidden">
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-white/20 rounded-lg mr-3">
                    <Layers className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Duplicate with Sizes
                    </h3>
                    <p className="text-teal-100 text-sm mt-0.5">
                      Select sizes to create variants
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDuplicateSizeModal(false);
                    setSelectedItemForDuplicate(null);
                    setDuplicateSizeSearch('');
                    setDuplicateSizeProgress(null);
                    setSelectedSizesForDuplicate(new Set(allStandardSizes));
                  }}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-colors"
                  disabled={!!duplicateSizeProgress}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {duplicateSizeProgress ? (
                <div className="py-8">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 dark:bg-teal-900/30 mb-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent"></div>
                    </div>
                    <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {duplicateSizeProgress.status}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {duplicateSizeProgress.current} of {duplicateSizeProgress.total} completed
                    </p>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-teal-500 to-cyan-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${(duplicateSizeProgress.current / duplicateSizeProgress.total) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {allStandardSizes.filter(s => selectedSizesForDuplicate.has(s)).map(size => (
                      <span key={size}>{size}</span>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Select Sizes to Create
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedSizesForDuplicate(new Set(allStandardSizes))}
                          className="text-xs px-2.5 py-1 rounded-lg bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-900/50 transition-colors font-medium"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedSizesForDuplicate(new Set())}
                          className="text-xs px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {allStandardSizes.map((size) => {
                        const isSelected = selectedSizesForDuplicate.has(size);
                        return (
                          <button
                            key={size}
                            type="button"
                            onClick={() => {
                              const newSelected = new Set(selectedSizesForDuplicate);
                              if (isSelected) {
                                newSelected.delete(size);
                              } else {
                                newSelected.add(size);
                              }
                              setSelectedSizesForDuplicate(newSelected);
                            }}
                            className={`relative px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 border-2 ${
                              isSelected
                                ? 'bg-gradient-to-br from-teal-500 to-cyan-500 text-white border-transparent shadow-md shadow-teal-500/25'
                                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-teal-400 dark:hover:border-teal-500'
                            }`}
                          >
                            <span className="relative z-10">{size}</span>
                            {isSelected && (
                              <div className="absolute top-0.5 right-0.5">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {selectedSizesForDuplicate.size} size{selectedSizesForDuplicate.size !== 1 ? 's' : ''} selected
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Search and select an item to duplicate
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={duplicateSizeSearch}
                        onChange={(e) => setDuplicateSizeSearch(e.target.value)}
                        placeholder="Search by name, batch, or category..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    <div className="max-h-80 overflow-y-auto">
                      {items
                        .filter(item => {
                          if (!duplicateSizeSearch) return true;
                          const search = duplicateSizeSearch.toLowerCase();
                          return (
                            item.name.toLowerCase().includes(search) ||
                            item.category.toLowerCase().includes(search) ||
                            item.batch?.toLowerCase().includes(search)
                          );
                        })
                        .slice(0, 50)
                        .map((item) => {
                          const isSelected = selectedItemForDuplicate?.id === item.id;
                          return (
                            <div
                              key={item.id}
                              onClick={() => setSelectedItemForDuplicate(item)}
                              className={`flex items-center p-4 cursor-pointer transition-all border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                                isSelected
                                  ? 'bg-teal-50 dark:bg-teal-900/30'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              <div className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden mr-4 ${
                                item.image_url ? '' : 'bg-gray-200 dark:bg-gray-600 flex items-center justify-center'
                              }`}>
                                {item.image_url ? (
                                  <img
                                    src={item.image_url}
                                    alt={item.name}
                                    crossOrigin="anonymous"
                                    className="w-full h-full object-contain bg-gray-50 dark:bg-gray-800"
                                  />
                                ) : (
                                  <Image className="h-6 w-6 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center">
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {item.name}
                                  </h4>
                                  {item.color_id && (
                                    <div
                                      className="ml-2 w-3 h-3 rounded-full border border-gray-300 dark:border-gray-600"
                                      style={{
                                        backgroundColor: colors.find(c => c.id === item.color_id)?.hex_code || '#ccc'
                                      }}
                                    />
                                  )}
                                  {item.size_id && (
                                    <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-gray-600 dark:text-gray-300">
                                      {sizes.find(s => s.id === item.size_id)?.name}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {item.category}
                                  </span>
                                  <span className="text-xs text-gray-400">|</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Qty: {item.quantity}
                                  </span>
                                  {item.sell_price && hasPricingAccess && (
                                    <>
                                      <span className="text-xs text-gray-400">|</span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {item.sell_price.toFixed(2)} EUR
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                isSelected
                                  ? 'border-teal-500 bg-teal-500'
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}>
                                {isSelected && <Check className="h-4 w-4 text-white" />}
                              </div>
                            </div>
                          );
                        })}
                      {items.filter(item => {
                        if (!duplicateSizeSearch) return true;
                        const search = duplicateSizeSearch.toLowerCase();
                        return (
                          item.name.toLowerCase().includes(search) ||
                          item.category.toLowerCase().includes(search) ||
                          item.batch?.toLowerCase().includes(search)
                        );
                      }).length === 0 && (
                        <div className="p-8 text-center">
                          <Package className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            No items found
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedItemForDuplicate && selectedSizesForDuplicate.size > 0 && (
                    <div className="mt-4 p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 p-2 bg-teal-100 dark:bg-teal-800/50 rounded-lg">
                          <Check className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                        </div>
                        <div className="ml-3 flex-1">
                          <h4 className="text-sm font-medium text-teal-900 dark:text-teal-100">
                            Ready to create size variants
                          </h4>
                          <p className="text-sm text-teal-700 dark:text-teal-300 mt-1">
                            This will create {selectedSizesForDuplicate.size} {selectedSizesForDuplicate.size === 1 ? 'copy' : 'copies'} of <strong>"{selectedItemForDuplicate.name}"</strong>
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {allStandardSizes.filter(s => selectedSizesForDuplicate.has(s)).map(size => (
                              <span
                                key={size}
                                className="inline-flex items-center px-2.5 py-1 bg-white dark:bg-gray-800 border border-teal-300 dark:border-teal-700 rounded-lg text-xs font-medium text-teal-700 dark:text-teal-300"
                              >
                                {size}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {!duplicateSizeProgress && (
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <Button
                  onClick={() => {
                    setShowDuplicateSizeModal(false);
                    setSelectedItemForDuplicate(null);
                    setDuplicateSizeSearch('');
                    setSelectedSizesForDuplicate(new Set(allStandardSizes));
                  }}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDuplicateWithSizes}
                  disabled={!selectedItemForDuplicate || selectedSizesForDuplicate.size === 0}
                  className={`${
                    selectedItemForDuplicate && selectedSizesForDuplicate.size > 0
                      ? 'bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white'
                      : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  } border-0`}
                >
                  <Layers className="h-4 w-4 mr-2" />
                  Create {selectedSizesForDuplicate.size} Size Variant{selectedSizesForDuplicate.size !== 1 ? 's' : ''}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {previewImage && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors flex items-center gap-2 text-sm"
            >
              <X className="h-5 w-5" />
              Close
            </button>
            <div className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="relative">
                <img
                  src={previewImage.url}
                  alt={previewImage.name}
                  crossOrigin="anonymous"
                  className="w-full h-auto max-h-[70vh] object-contain"
                />
              </div>
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{previewImage.name}</h3>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};